/**
 * Money helpers -- phase 6b, Task 1.
 *
 * The API speaks 2dp strings (`"1234.50"`); the UI displays them and hands
 * them back. No arithmetic happens here: `Number()` is used only to split a
 * string into thousands groups for DISPLAY, never to compute a value, and
 * `toMoneyString` refuses (throws) rather than rounds when an input carries
 * more than two decimals.
 */

/** `'1234.5'` -> `'R 1 234.50'`; `null`/`undefined`/non-numeric -> `'—'`. */
export function formatRand(value: string | null | undefined): string {
  if (value == null || value === '') return '—';
  if (!/^-?\d+(\.\d+)?$/.test(value.trim())) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const [whole, frac] = Math.abs(n).toFixed(2).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${n < 0 ? '-' : ''}R ${grouped}.${frac}`;
}

/**
 * Normalise a text-field value to the API's 2dp string.
 * `''` (or whitespace) -> `null` (the "unset" the nullable money fields expect);
 * `'12'` -> `'12.00'`; `'12.5'` -> `'12.50'`; `'.5'` -> `'0.50'`.
 * Throws on anything else -- negative, more than 2dp, separators, letters --
 * so a form can surface it as a field error instead of silently rounding.
 */
export function toMoneyString(input: string): string | null {
  const s = input.trim();
  if (s === '') return null;
  const m = /^(\d*)(?:\.(\d{0,2}))?$/.exec(s);
  if (!m || (m[1] === '' && (m[2] === undefined || m[2] === ''))) {
    throw new Error(`Invalid amount: "${input}" (digits with up to two decimals, e.g. 12.50)`);
  }
  const whole = m[1] === '' ? '0' : m[1];
  const frac = (m[2] ?? '').padEnd(2, '0');
  return `${whole}.${frac}`;
}
